import { useEffect, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { AppBar, Box, Toolbar } from '@mui/material';
import clsx from 'clsx';
import * as R from 'ramda';

import { requestUserOrganizations } from '@atom/actions/organizationActions';
import { triggerLogout } from '@atom/actions/userActions';
import Logo from '@atom/components/common/navigation/Logo';
import { useNavigation } from '@atom/hooks/useNavigation';
import { Progress } from '@atom/mui';
import { getUserTenantsSelector } from '@atom/selectors/userSelectors';
import colors from '@atom/styles/colors';
import layoutStyles from '@atom/styles/layout';
import { RootState } from '@atom/types/store';
import accountUtilities from '@atom/utilities/accountUtilities';

import LoginFooter from './LoginFooter';
import TenantSelectionRow from './TenantSelectionRow';

import classes from './login.module.css';

const navBarStyle = {
  height: layoutStyles.topNavHeight,
  backgroundColor: colors.neutral.white,
  boxShadow: '0 2px 4px 1px',
  color: colors.utility.boxShadow,
  position: 'fixed',
  top: 0,
};

export interface Organization {
  domain: string;
  id: string;
  isActive: boolean;
  isVerified: boolean;
  name: string;
  role: string;
}

const TenantSelection = () => {
  const [, { Link, navigate }] = useNavigation();
  const email = accountUtilities.getLoggedInEmail();

  if (!email) {
    navigate('/login');
  }

  const dispatch = useDispatch();
  const organizations = useSelector(getUserTenantsSelector);

  // Moves local-acme to top of list
  const sortedOrganizations = useMemo(() => {
    const localIndex = R.findIndex(
      R.propEq('local-acme', 'name'),
      organizations,
    );

    return R.move(localIndex, 0, organizations);
  }, [organizations]);

  const loading = useSelector(
    (state: RootState) => state.loading.loadingOrganizations,
  );

  useEffect(() => {
    dispatch(requestUserOrganizations());
  }, []);

  const logout = () => {
    dispatch(triggerLogout());
  };

  const content = loading ? (
    <div className={classes.spinnerContainer}>
      <Progress />
    </div>
  ) : (
    <div className={classes.tenantsContainer}>
      {sortedOrganizations.map((organization: Organization, index: number) => (
        <TenantSelectionRow
          key={`${organization.id}-${index}`}
          organization={organization}
        />
      ))}
      <div className={classes.tenantSelectionContainer}>
        <div>Cannot find the team you are looking for?</div>
        <div className={classes.tenantDecisionContainer}>
          <Link to="/tenant-entry">
            <div className={classes.tenantSelectionLink}>Join a workspace</div>
          </Link>
          or
          <Link to="/tenant-creation">
            <div className={classes.tenantSelectionLink}>
              Create a workspace
            </div>
          </Link>
        </div>
      </div>
    </div>
  );

  return (
    <>
      <AppBar style={navBarStyle}>
        <Toolbar>
          <div>
            <Logo />
          </div>
          <Box sx={{ flexGrow: 1 }} />
          <div className={classes.navRightContainer}>
            <div className={classes.navRightText}>{email}</div>
            <div className={classes.navRightLogin} onClick={logout}>
              Sign Out
            </div>
          </div>
        </Toolbar>
      </AppBar>
      <div className={classes.container}>
        <div className={clsx(classes.authBackground, classes.partial)} />
        <div className={classes.headingText}>Choose a workspace</div>
        {content}
        <LoginFooter />
      </div>
    </>
  );
};

export default TenantSelection;
